import IconTrash1 from "@/icons/IconTrash1";
import React, { useId } from "react";
import { IntegrationFieldMap } from "./IntegrationMappingPanel";
import { IconButton } from "@/components/ds";

export interface Option {
  value: any;
  label: string;
  key?: string;
  disabled?: boolean;
}

interface Props {
  mappedField: IntegrationFieldMap;
  i: number;
  fieldLabel: string;
  updateMappedField: (i: number, field: IntegrationFieldMap) => void;
  metadata: Option[];
  isLoading: boolean;
  deleteRow: (i: number) => void;
  /** Inline validation error (e.g. a duplicate field selection) for this row. */
  errorMessage?: string;
  /** Field values already mapped by OTHER rows in this scope — disabled here so a
   * field can't be picked twice (Issue S6: no duplicate-field validation). */
  usedValues?: Set<any>;
}

export default function IntegrationMappingRow(props: Props) {
  const {
    mappedField,
    i,
    fieldLabel,
    metadata,
    isLoading,
    errorMessage,
    usedValues
  } = props;
  const errorId = useId();

  // "All changes saved" is now surfaced by the panel itself, only once a
  // save genuinely reaches the server and succeeds (see IntegrationMappingPanel's
  // saveRecord) — never optimistically here, which previously showed "saved"
  // even for an edit that got rejected (a duplicate field) or failed to POST.
  const handleChange = (callback: () => void) => {
    callback();
  };

  return (
    <tr key={i}>
      <td className="pb-4 pr-5 pl-4">
        <div
          className={`relative text-zinc-800 text-sm font-normal border rounded-lg ${
            errorMessage ? "border-red-500" : "border-zinc-300"
          }`}
        >
          <select
            className="w-full text-sm font-normal bg-white rounded-lg border-none px-4 py-2 appearance-none focus:outline-none focus:ring-0"
            value={mappedField.field?.value || ""}
            aria-label={`${fieldLabel} selection`}
            aria-invalid={!!errorMessage}
            aria-describedby={errorMessage ? errorId : undefined}
            onChange={(e) =>
              handleChange(() =>
                props.updateMappedField(i, {
                  ...mappedField,
                  field: {
                    value: e.target.value,
                    label: e.target.options[e.target.selectedIndex].text
                  }
                })
              )
            }
            disabled={isLoading}
          >
            {isLoading ? (
              <option>Loading...</option>
            ) : (
              <>
                <option value="" disabled={!!mappedField.field?.value}>
                  Select a field…
                </option>
                {metadata.map((option) => {
                  // Never disable the value THIS row already has selected, even if
                  // `usedValues` (built by the panel from sibling rows) happens to
                  // contain it too — that can legitimately happen for data saved
                  // before duplicate-field validation existed (Issue S6). Without
                  // this, a pre-existing duplicate row would show its own current
                  // selection as disabled/"(already mapped)", making it look
                  // broken/unselectable instead of just flagging the OTHER row.
                  const isOwnSelection = option.value === mappedField.field?.value;
                  const isUsedElsewhere =
                    !isOwnSelection && !!usedValues?.has(option.value);
                  return (
                    <option
                      key={option.value}
                      value={option.value}
                      disabled={isUsedElsewhere}
                    >
                      {option.label}
                      {isUsedElsewhere ? " (already mapped)" : ""}
                    </option>
                  );
                })}
              </>
            )}
          </select>
          <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none flex items-center text-zinc-400">
            <svg
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d="M3 4.5L6 7.5L9 4.5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </div>
        {errorMessage && (
          <p id={errorId} role="alert" className="mt-1 text-xs text-red-600">
            {errorMessage}
          </p>
        )}
      </td>
      <td className="pb-4">
        <input
          className="w-full h-max focus:outline-none p-2 text-zinc-800 text-sm font-normal border border-zinc-300 rounded-lg"
          value={mappedField.defaultValue || ""}
          placeholder="Enter default value"
          aria-label="Default value"
          onChange={(e) =>
            handleChange(() =>
              props.updateMappedField(i, {
                ...mappedField,
                defaultValue: e.currentTarget.value
              })
            )
          }
        />
      </td>
      <td className="pb-4">
        <input
          type="checkbox"
          className="input w-full"
          checked={mappedField.create}
          aria-label="Create"
          onChange={() =>
            handleChange(() =>
              props.updateMappedField(i, {
                ...mappedField,
                create: !mappedField.create
              })
            )
          }
        />
      </td>
      <td className="pb-4">
        <input
          type="checkbox"
          className="input w-full"
          checked={mappedField.usedForSearching}
          aria-label="Used for searching"
          onChange={() =>
            handleChange(() =>
              props.updateMappedField(i, {
                ...mappedField,
                usedForSearching: !mappedField.usedForSearching
              })
            )
          }
        />
      </td>
      <td className="pb-4">
        <input
          type="checkbox"
          className="input w-full"
          checked={mappedField.showWhenSelected}
          aria-label="Show when selected"
          onChange={() =>
            handleChange(() =>
              props.updateMappedField(i, {
                ...mappedField,
                showWhenSelected: !mappedField.showWhenSelected
              })
            )
          }
        />
      </td>
      <td className="pb-4">
        <input
          type="checkbox"
          className="input w-full"
          checked={mappedField.showInSearch}
          aria-label="Show in search"
          onChange={() =>
            handleChange(() =>
              props.updateMappedField(i, {
                ...mappedField,
                showInSearch: !mappedField.showInSearch
              })
            )
          }
        />
      </td>
      <td className="text-center justify-center items-center place-self-center">
        <IconButton
          onClick={() => props.deleteRow(i)}
          aria-label="Delete field mapping"
        >
          <IconTrash1 />
        </IconButton>
      </td>
    </tr>
  );
}
